import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { resolveIcon } from '../../components/common/IconResolver';
import ViewShell from './ViewShell';
import DemoPublishCard from '../../components/demos/DemoPublishCard';
import DemoStatusStrip from '../../components/demos/DemoStatusStrip';
import { useDemoStatus } from '../../hooks/useDemoStatus';
import { DEMO_COMPONENTS, type ComponentState, type DemoComponent } from '../../services/demoStatusService';
import type { ViewContext } from '../StubPage';
import {
  ensureDemoSession,
  fetchManifest,
  validateManifest,
  KIND_LABELS,
  type DemoEntry,
} from '../../services/demoCenterService';

interface DemoCenterPageProps {
  context?: ViewContext;
}

type LoadState =
  | 'loading'
  | 'not-entitled'
  | 'session-error'
  | 'unavailable'
  | 'invalid-manifest'
  | 'wrong-host'
  | 'empty'
  | 'ready';

// Customer-facing copy (DC-11): no internal vocabulary in anything shown here.
const STATE_COPY: Record<Exclude<LoadState, 'ready'>, { icon: string; title: string; detail?: string; tone: string }> = {
  loading: { icon: 'monitor', title: 'Loading demos…', tone: 'var(--text-secondary)' },
  'not-entitled': {
    icon: 'lock',
    title: 'The Demo Center is available to IWT users.',
    tone: 'var(--text-secondary)',
  },
  'session-error': {
    icon: 'alert-triangle',
    title: "We couldn't open the Demo Center.",
    detail: 'Please try again in a moment.',
    tone: 'var(--warning)',
  },
  unavailable: {
    icon: 'monitor',
    title: 'Demo content is not deployed yet.',
    detail: 'Check back soon.',
    tone: 'var(--text-secondary)',
  },
  'invalid-manifest': {
    icon: 'alert-circle',
    title: "The list of demos couldn't be read.",
    detail: 'Please try again later.',
    tone: 'var(--error)',
  },
  // WQ-346: the page was opened on a host that doesn't serve /demos; detail + link come from the ticket.
  'wrong-host': { icon: 'monitor', title: "The Demo Center opens on IWT's site.", tone: 'var(--accent-primary)' },
  empty: { icon: 'layout-dashboard', title: 'No demos published yet.', tone: 'var(--text-secondary)' },
};

interface StatePanelProps {
  state: Exclude<LoadState, 'ready'>;
  onRetry?: () => void;
  detail?: string;
  action?: React.ReactNode;
}

const StatePanel: React.FC<StatePanelProps> = ({ state, onRetry, detail, action }) => {
  const copy = { ...STATE_COPY[state], ...(detail ? { detail } : {}) };
  return (
    <div
      className="card"
      data-testid={`demo-center-state-${state}`}
      role={state === 'loading' ? 'status' : undefined}
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center',
        gap: 'var(--space-3, 12px)',
        padding: 'var(--space-8, 32px) var(--space-6, 24px)',
      }}
    >
      <div
        className={state === 'loading' ? 'spinner' : undefined}
        style={{
          width: 48,
          height: 48,
          borderRadius: 'var(--radius-full, 9999px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: copy.tone,
          background: 'var(--bg-tertiary, rgba(148, 163, 184, 0.1))',
        }}
      >
        {resolveIcon(copy.icon, 24)}
      </div>
      <div style={{ fontSize: 'var(--text-md, 15px)', fontWeight: 600, color: 'var(--text-primary)' }}>
        {copy.title}
      </div>
      {copy.detail && (
        <div style={{ fontSize: 'var(--text-sm, 13px)', color: 'var(--text-secondary)' }}>{copy.detail}</div>
      )}
      {onRetry && (
        <button type="button" className="btn btn-secondary btn-sm" onClick={onRetry}>
          {resolveIcon('refresh-cw', 14)}
          Retry
        </button>
      )}
      {action}
    </div>
  );
};

// WQ-348: tile badge for demos whose live features need a server component (DEMO_COMPONENTS).
const COMPONENT_BADGE_LABELS: Record<string, string> = { 'cad-ingest': 'Live CAD ingest' };
// not_live: the component isn't deployed yet (before go-live) - no badge rather than a red one.
const BADGE_STATE: Record<ComponentState, { text: string; cls: string } | null> = {
  ok: { text: 'running', cls: 'badge badge-success' },
  degraded: { text: 'degraded', cls: 'badge badge-warning' },
  down: { text: 'unavailable', cls: 'badge badge-error' },
  not_live: null,
};

// Keyed by the demo's mount under /demos/ (SENTINEL is /demos/sentinel/, where its ingest API lives), slug as fallback.
const mountOf = (path: string) => path.split('/')[2] ?? '';

const ComponentBadges: React.FC<{ slug: string; path: string; components?: DemoComponent[] }> = ({ slug, path, components }) => {
  const needed = DEMO_COMPONENTS[mountOf(path)] ?? DEMO_COMPONENTS[slug];
  if (!needed || !components) return null;
  const badges = components.flatMap((c) => {
    const badge = needed.includes(c.id) ? BADGE_STATE[c.state] : null;
    return badge ? [{ c, badge }] : [];
  });
  if (!badges.length) return null;
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
      {badges.map(({ c, badge }) => (
        <span key={c.id} className={badge.cls} data-testid={`demo-card-${slug}-${c.id}`}>
          {COMPONENT_BADGE_LABELS[c.id] ?? c.label} · {badge.text}
        </span>
      ))}
    </div>
  );
};

const DemoCard: React.FC<{ demo: DemoEntry; onOpen: (demo: DemoEntry) => void; components?: DemoComponent[] }> = ({
  demo,
  onOpen,
  components,
}) => (
  <div
    className="card card-elevated"
    data-testid={`demo-card-${demo.slug}`}
    style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3, 12px)', minWidth: 0 }}
  >
    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 'var(--space-3, 12px)' }}>
      <h3
        style={{
          margin: 0,
          fontSize: 'var(--text-lg, 16px)',
          fontWeight: 600,
          color: 'var(--text-primary)',
          overflowWrap: 'anywhere',
        }}
      >
        {demo.title}
      </h3>
      <span className={demo.kind === 'application' ? 'badge badge-accent' : 'badge badge-info'} style={{ flexShrink: 0 }}>
        {KIND_LABELS[demo.kind]}
      </span>
    </div>
    <p
      style={{
        margin: 0,
        flex: 1,
        fontSize: 'var(--text-sm, 13px)',
        lineHeight: 1.5,
        color: 'var(--text-secondary)',
        overflowWrap: 'anywhere',
      }}
    >
      {demo.description}
    </p>
    <ComponentBadges slug={demo.slug} path={demo.path} components={components} />
    <div>
      <button
        type="button"
        className="btn btn-primary btn-sm"
        onClick={() => onOpen(demo)}
        aria-label={`Open ${demo.title}`}
      >
        Open
      </button>
    </div>
  </div>
);

const EmbedPanel: React.FC<{ demo: DemoEntry; onClose: () => void }> = ({ demo, onClose }) => (
  <section
    className="card"
    data-testid="demo-embed-panel"
    aria-label={demo.title}
    style={{ marginTop: 'var(--space-6, 24px)', padding: 'var(--space-4, 16px)', transform: 'none' }}
  >
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: 'var(--space-3, 12px)',
        marginBottom: 'var(--space-3, 12px)',
      }}
    >
      <h3 style={{ margin: 0, fontSize: 'var(--text-md, 15px)', fontWeight: 600, color: 'var(--text-primary)', overflowWrap: 'anywhere' }}>
        {demo.title}
      </h3>
      <div style={{ display: 'flex', gap: 'var(--space-2, 8px)' }}>
        <a className="btn btn-secondary btn-sm" href={demo.path} target="_blank" rel="noopener">
          Open in new tab
        </a>
        <button type="button" className="btn btn-ghost btn-sm" onClick={onClose}>
          Close
        </button>
      </div>
    </div>
    <iframe
      src={demo.path}
      title={demo.title}
      style={{
        display: 'block',
        width: '100%',
        height: '75vh',
        border: '1px solid var(--glass-border, rgba(148, 163, 184, 0.2))',
        borderRadius: 'var(--radius-lg, 12px)',
        background: 'var(--bg-primary)',
      }}
    />
  </section>
);

const DemoCenterPage: React.FC<DemoCenterPageProps> = ({ context }) => {
  const { authFetch } = useAuth();
  // WQ-348 (DC-17): server component status for every entitled user; refreshes while visible.
  const { result: statusResult } = useDemoStatus(authFetch);
  const liveStatus = statusResult?.kind === 'ok' ? statusResult.status : null;
  const [state, setState] = useState<LoadState>('loading');
  const [demos, setDemos] = useState<DemoEntry[]>([]);
  const [embedded, setEmbedded] = useState<DemoEntry | null>(null);
  const [demoOrigin, setDemoOrigin] = useState<string | null>(null);
  const runId = useRef(0);

  const load = useCallback(async () => {
    const run = ++runId.current;
    const settle = (next: LoadState, list: DemoEntry[] = [], origin: string | null = null) => {
      if (run !== runId.current) return; // a newer load (Retry) or unmount superseded this one
      setDemos(list);
      setDemoOrigin(origin);
      setState(next);
    };
    setState('loading');
    setEmbedded(null);

    const session = await ensureDemoSession(authFetch);
    if (session.status === 'wrong-host') return settle('wrong-host', [], session.origin);
    if (session.status !== 'ok') return settle(session.status);

    const manifest = await fetchManifest();
    if (manifest.status !== 'ok') return settle(manifest.status);

    const { valid, demos: list } = validateManifest(manifest.raw);
    if (!valid) return settle('invalid-manifest');
    settle(list.length ? 'ready' : 'empty', list);
  }, [authFetch]);

  useEffect(() => {
    load();
    return () => {
      runId.current += 1;
    };
  }, [load]);

  const open = (demo: DemoEntry) => {
    if (demo.open === 'embed') {
      setEmbedded(demo);
    } else {
      window.open(demo.path, '_blank', 'noopener');
    }
  };

  return (
    <ViewShell
      context={context}
      title="Demo Center"
      icon="monitor"
      subtitle="Product demonstrations and interactive dashboards"
    >
      {liveStatus && <DemoStatusStrip status={liveStatus} />}
      {state === 'ready' ? (
        <>
          <div
            data-testid="demo-center-grid"
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(min(280px, 100%), 1fr))',
              gap: 'var(--space-4, 16px)',
            }}
          >
            {demos.map((demo) => (
              <DemoCard key={demo.slug} demo={demo} onOpen={open} components={liveStatus?.components} />
            ))}
          </div>
          {embedded && <EmbedPanel demo={embedded} onClose={() => setEmbedded(null)} />}
        </>
      ) : (
        <StatePanel
          state={state}
          onRetry={state === 'session-error' || state === 'invalid-manifest' ? load : undefined}
          detail={state === 'wrong-host' && demoOrigin
            ? `Open it at ${new URL(demoOrigin).hostname} — you may need to sign in there once.`
            : undefined}
          action={state === 'wrong-host' && demoOrigin ? (
            <a className="btn btn-primary btn-sm" href={`${demoOrigin}/demo-center-home`}>
              Open Demo Center
            </a>
          ) : undefined}
        />
      )}
      {/* WQ-338: publishers only — renders nothing unless /status says can_publish */}
      <DemoPublishCard title="Manage" components={liveStatus?.components ?? []} />
    </ViewShell>
  );
};

export default DemoCenterPage;
